import { FullscreenOverlayContainer, OverlayContainer } from '@angular/cdk/overlay';
import { provideHttpClient, withInterceptors } from '@angular/common/http';
import {
    APP_INITIALIZER,
    ApplicationConfig,
    ErrorHandler,
    inject,
} from '@angular/core';
import { LuxonDateAdapter } from '@angular/material-luxon-adapter';
import {
    DateAdapter,
    MAT_DATE_FORMATS,
} from '@angular/material/core';
import { MatPaginatorIntl } from '@angular/material/paginator';
import { provideAnimations } from '@angular/platform-browser/animations';
import {
    PreloadAllModules,
    provideRouter,
    withInMemoryScrolling,
    withPreloading,
} from '@angular/router';
import { provideFuse } from '@fuse';
import {
    applyPersistedBodySchemeAndTheme,
    CUSTOM_DATE_FORMATS,
    getPersistedSchemeAndTheme,
    provideSaiI18n,
    SAI_FUSE_SCREENS,
    SAI_FUSE_THEMES,
} from 'app/core/bootstrap/sai-bootstrap';
import { appRoutes } from 'app/app.routes';
import { authInterceptor } from 'app/core/auth/auth.interceptor';
import { quotaErrorInterceptor } from 'app/modules/shared/interceptors/quota-error.interceptor';
import { provideAuth } from 'app/core/auth/auth.provider';
import { provideIcons } from 'app/core/icons/icons.provider';
import { fuseLoadingInterceptor } from '@fuse/services/loading';
import { mockApiServices } from 'app/mock-api';
import { ArabicPaginatorIntl } from '../arabic-paginator-intl';
import { MAT_TOOLTIP_DEFAULT_OPTIONS, MatTooltipDefaultOptions } from '@angular/material/tooltip';
import { environment } from 'environments/environment';
import { ChunkErrorHandler } from 'app/core/chunk-error.handler';
import { VersionCheckService } from 'app/core/version-check/version-check.service';
import { CHAT_CHART_DIALOG_LOADER } from 'app/ui/chat-kit/chat-result-dialog.service';

const instantTooltipOptions: MatTooltipDefaultOptions = {
    showDelay: 0,
    hideDelay: 0,
    touchendHideDelay: 1500,
    disableTooltipInteractivity: true
};

export { CUSTOM_DATE_FORMATS };

export const appConfig: ApplicationConfig = {
    providers: [
        // Auto-recover a stale client from post-deploy ChunkLoadError via one reload.
        { provide: ErrorHandler, useClass: ChunkErrorHandler },
        provideAnimations(),
        // Dialogs, menus and tooltips follow an element into browser fullscreen (agents Tree View, charts)
        // instead of staying behind it on the body. Identical to the default container otherwise.
        { provide: OverlayContainer, useClass: FullscreenOverlayContainer },
        provideHttpClient(
            withInterceptors([authInterceptor, quotaErrorInterceptor, fuseLoadingInterceptor])
        ),
        provideRouter(
            appRoutes,
            withPreloading(PreloadAllModules),
            withInMemoryScrolling({ scrollPositionRestoration: 'enabled' })
        ),

        // The dashboard's full chart dialog (create tracker, favourite, full page) for AI chat and team chat result dialogs.
        {
            provide: CHAT_CHART_DIALOG_LOADER,
            useValue: () =>
                import('app/company/ai-chat/agent-chat/charts/message-chart/message-chart.component').then(m => m.MessageChartComponent),
        },

        // Material Date Adapter
        { provide: DateAdapter, useClass: LuxonDateAdapter },
        { provide: MAT_DATE_FORMATS, useValue: CUSTOM_DATE_FORMATS },
        { provide: MAT_TOOLTIP_DEFAULT_OPTIONS, useValue: instantTooltipOptions },

        // Material Paginator Internationalization
        { provide: MatPaginatorIntl, useClass: ArabicPaginatorIntl },

        // Locale, Transloco and the persisted-language initializer (shared with the messenger app).
        ...provideSaiI18n(),
        {
            provide: APP_INITIALIZER,
            multi: true,
            useFactory: () => (): void => applyPersistedBodySchemeAndTheme(),
        },
        {
            // Polls the deployed version.json and reloads a tab still running an older bundle,
            // so a new deploy reaches open pages instead of waiting for the next hard refresh.
            provide: APP_INITIALIZER,
            multi: true,
            useFactory: () => {
                const versionCheck = inject(VersionCheckService);
                return (): void => versionCheck.start();
            },
        },

        // Fuse
        provideAuth(),
        provideIcons(),

        provideFuse({
            mockApi: {
                delay: 0,
                services: mockApiServices,
            },
            fuse: {
                layout: 'classy',
                ...getPersistedSchemeAndTheme(),
                screens: SAI_FUSE_SCREENS,
                themes: SAI_FUSE_THEMES,
            },
        }),
    ],
};


